import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Grid
} from '@mui/material';
import {
  FactCheck as FactCheckIcon,
  Search as SearchIcon,
  AccountBalance as AccountBalanceIcon,
  Store as StoreIcon,
  Download as DownloadIcon,
  QueryStats as QueryStatsIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import api from '../../config/axiosConfig';
import { QA_STATUS_LABELS as STATUS_LABELS } from '../../utils/documentQaStatus';
import RevisionCalidadDialog from './RevisionCalidadDialog';

const DOC_LABELS = {
  PIPC: 'PIPC',
  REPORTE_INSPECCION: 'RIC',
  ACTA_CONSTITUTIVA_UIPC: 'Acta Constitutiva',
  CARTA_CORRESPONSABILIDAD: 'Carta de Corresponsabilidad'
};
const docLabel = (key) => DOC_LABELS[key] || (key?.startsWith('CONSTANCIAS_CURSO_') ? 'Constancias' : key);

const formatPercent = (value) => (value === null || value === undefined) ? '—' : `${Math.round(value * 100)}%`;
const formatHours = (value) => (value === null || value === undefined) ? '—' : `${value < 1 ? Math.round(value * 60) + ' min' : value.toFixed(1) + ' h'}`;

const StatCard = ({ label, value, color }) => (
  <Grid size={{ xs: 6, sm: 4, md: 3 }}>
    <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#fafafa', border: '1px solid #eee', height: '100%', boxSizing: 'border-box' }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{label}</Typography>
      <Typography variant="h6" fontWeight={700} sx={{ color: color || '#333' }}>{value}</Typography>
    </Box>
  </Grid>
);

const QaMetricsPanel = ({ metrics, loading }) => {
  if (loading) {
    return (
      <Paper sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress size={22} />
      </Paper>
    );
  }
  if (!metrics) return null;

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <QueryStatsIcon fontSize="small" sx={{ color: '#7b1fa2' }} /> Métricas de Calidad
      </Typography>
      <Grid container spacing={1.5} sx={{ mb: metrics.reviewers?.length ? 2 : 0 }}>
        <StatCard label="Documentos" value={metrics.totalDocuments} />
        <StatCard label="Pendientes" value={metrics.pendientes} color="#e65100" />
        <StatCard label="Aprobados" value={metrics.aprobados} color="#2e7d32" />
        <StatCard label="Rechazo ronda 1" value={formatPercent(metrics.round1RejectionRate)} color="#c62828" />
        <StatCard label="Rechazo ronda 2" value={formatPercent(metrics.round2RejectionRate)} color="#c62828" />
        <StatCard label="Tiempo prom. ronda 1" value={formatHours(metrics.avgRound1Hours)} />
        <StatCard label="Tiempo prom. ronda 2" value={formatHours(metrics.avgRound2Hours)} />
      </Grid>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: metrics.reviewers?.length ? 1.5 : 0 }}>
        El tiempo promedio solo considera el ciclo de revisión vigente de cada documento (se reinicia si el archivo se regenera).
      </Typography>
      {metrics.reviewers && metrics.reviewers.length > 0 && (
        <TableContainer sx={{ border: '1px solid #eee', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 700 }}>Revisor</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Aprobados</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Rechazados</TableCell>
                <TableCell sx={{ fontWeight: 700 }} align="center">Tasa de rechazo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {metrics.reviewers.map(r => (
                <TableRow key={r.reviewerId}>
                  <TableCell>{r.reviewerName}</TableCell>
                  <TableCell align="center">{r.approvedCount}</TableCell>
                  <TableCell align="center">{r.rejectedCount}</TableCell>
                  <TableCell align="center">{formatPercent(r.rejectionRate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

const RevisionCalidad = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState('pendientes');
  const [metrics, setMetrics] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  // Documento abierto en el diálogo de revisión (null = cerrado)
  const [openId, setOpenId] = useState(null);

  // Carpeta de Gestión Documental desde la que se llegó acá (chip de estado QA en
  // CentroGestionDocumental.jsx), si fue así -- se captura ANTES de que el efecto de abajo pise
  // location.state con el replace que limpia el ?doc=. Sostiene el botón "Volver a Gestión
  // Documental" mientras dure esta sesión de la pantalla (se pierde si se recarga la página,
  // igual que cualquier location.state).
  const [returnPath, setReturnPath] = useState(undefined);

  useEffect(() => {
    fetchAll();
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    setMetricsLoading(true);
    try {
      const res = await api.get('/document-qa/metrics');
      setMetrics(res.data || null);
    } catch (e) {
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  };

  // Llegar desde Gestión Documental con ?doc=<quotationDocumentId> abre ese documento directo en
  // el diálogo de revisión.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const docId = params.get('doc');
    if (!docId) return;
    setOpenId(Number(docId));
    if (location.state?.returnPath !== undefined) {
      setReturnPath(location.state.returnPath);
    }
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // Reabre Gestión Documental directo en la carpeta desde la que se llegó (mismo mecanismo
  // location.state.initialPath que ya usa CentroGestionDocumental para "Ver en Gestión
  // Documental" tras generar un RIC/PIPC).
  const handleReturnToDocuments = () => {
    navigate('/servicios/documentos', { state: { initialPath: returnPath } });
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const res = await api.get('/document-qa');
      setItems(res.data || []);
    } catch (e) {
      setSnackbar({ open: true, message: 'Error al cargar documentos para revisión', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Mientras haya comparaciones corriendo en segundo plano, refrescar la lista sin parpadeo
  useEffect(() => {
    if (!items.some(i => i.fileComparisonRunning)) return undefined;
    const timer = setTimeout(async () => {
      try {
        const res = await api.get('/document-qa');
        setItems(res.data || []);
      } catch { /* se reintenta en el próximo refresco */ }
    }, 15000);
    return () => clearTimeout(timer);
  }, [items]);

  const currentRound = (item) => {
    if (!item) return null;
    if (item.qaStatus === 'PENDIENTE_REVISION' || item.qaStatus === 'REVISION_1_RECHAZADA') return 1;
    if (item.qaStatus === 'REVISION_1_APROBADA' || item.qaStatus === 'REVISION_2_RECHAZADA') return 2;
    return null; // REVISION_2_APROBADA: ya no hay ronda pendiente
  };

  // Fuerza descarga directa en vez de la vista previa de Dropbox (dl=0 -> dl=1)
  const getDownloadLink = (dropboxLink) => {
    if (!dropboxLink) return null;
    if (/[?&]dl=1/.test(dropboxLink)) return dropboxLink;
    if (/[?&]dl=0/.test(dropboxLink)) return dropboxLink.replace(/dl=0/, 'dl=1');
    return dropboxLink + (dropboxLink.includes('?') ? '&dl=1' : '?dl=1');
  };

  const formatDateTime = (value) => {
    if (!value) return '—';
    const d = new Date(value);
    return d.toLocaleString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const matchesStatusFilter = (i) => {
    if (statusFilter === 'aprobados') return i.qaStatus === 'REVISION_2_APROBADA';
    if (statusFilter === 'pendientes') return currentRound(i) !== null;
    return true; // 'todos'
  };

  const pendientesCount = items.filter(i => currentRound(i) !== null).length;
  const aprobadosCount = items.filter(i => i.qaStatus === 'REVISION_2_APROBADA').length;

  const filtered = items.filter(i => {
    if (!matchesStatusFilter(i)) return false;
    const search = filterText.toLowerCase();
    return (
      (i.companyName || '').toLowerCase().includes(search) ||
      (i.entityName || '').toLowerCase().includes(search) ||
      (i.workOrderFolio || '').toLowerCase().includes(search) ||
      (docLabel(i.documentKey) || '').toLowerCase().includes(search) ||
      (i.assignedUserName || '').toLowerCase().includes(search)
    );
  });

  const handleSortRequest = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortValue = (i, field) => {
    switch (field) {
      case 'orden': return i.workOrderFolio || '';
      case 'empresa': return i.companyName || '';
      case 'documento': return docLabel(i.documentKey) || '';
      case 'ejecutivo': return i.assignedUserName || '';
      case 'estado': return i.qaStatus || '';
      case 'subido': return i.uploadedAt || '';
      default: return '';
    }
  };

  const sorted = !sortField ? filtered : [...filtered].sort((a, b) => {
    const valueA = getSortValue(a, sortField).toString().toLowerCase();
    const valueB = getSortValue(b, sortField).toString().toLowerCase();
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ p: 3 }}>
      {returnPath !== undefined && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleReturnToDocuments}
          size="small"
          sx={{ textTransform: 'none', mb: 1.5 }}
        >
          Volver a Gestión Documental
        </Button>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <FactCheckIcon sx={{ color: '#7b1fa2', fontSize: 32 }} />
        <Box>
          <Typography variant="h5" fontWeight="bold">Revisión de Calidad</Typography>
          <Typography variant="body2" color="text.secondary">
            Dos rondas de revisión para cada RIC, PIPC, Acta Constitutiva y Carta de Corresponsabilidad generados antes de darlos por buenos
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar por orden, empresa, ente, ejecutivo o documento..."
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            sx={{ width: 350 }}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9e9e9e' }} /></InputAdornment>
            }}
          />
          <ToggleButtonGroup
            size="small"
            exclusive
            value={statusFilter}
            onChange={(e, val) => val && setStatusFilter(val)}
          >
            <ToggleButton value="pendientes" sx={{ textTransform: 'none', '&.Mui-selected': { bgcolor: '#fff3e0', '&:hover': { bgcolor: '#ffe0b2' } } }}>
              Pendientes ({pendientesCount})
            </ToggleButton>
            <ToggleButton value="aprobados" sx={{ textTransform: 'none', '&.Mui-selected': { bgcolor: '#e8f5e9', '&:hover': { bgcolor: '#c8e6c9' } } }}>
              Aprobados ({aprobadosCount})
            </ToggleButton>
            <ToggleButton value="todos" sx={{ textTransform: 'none', '&.Mui-selected': { bgcolor: '#ede7f6', '&:hover': { bgcolor: '#d1c4e9' } } }}>
              Todos ({items.length})
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Paper>

      <QaMetricsPanel metrics={metrics} loading={metricsLoading} />

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortField === 'orden'} direction={sortField === 'orden' ? sortDirection : 'asc'} onClick={() => handleSortRequest('orden')}>
                  Orden
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortField === 'empresa'} direction={sortField === 'empresa' ? sortDirection : 'asc'} onClick={() => handleSortRequest('empresa')}>
                  Empresa
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Ente</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortField === 'ejecutivo'} direction={sortField === 'ejecutivo' ? sortDirection : 'asc'} onClick={() => handleSortRequest('ejecutivo')}>
                  Ejecutivo
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortField === 'documento'} direction={sortField === 'documento' ? sortDirection : 'asc'} onClick={() => handleSortRequest('documento')}>
                  Documento
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortField === 'subido'} direction={sortField === 'subido' ? sortDirection : 'asc'} onClick={() => handleSortRequest('subido')}>
                  Generado
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel active={sortField === 'estado'} direction={sortField === 'estado' ? sortDirection : 'asc'} onClick={() => handleSortRequest('estado')}>
                  Estado
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Archivo vs datos</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress size={32} /></TableCell></TableRow>
            ) : sorted.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                {filterText ? 'No se encontraron resultados' : 'No hay documentos generados todavía'}
              </TableCell></TableRow>
            ) : sorted.map(i => {
              const statusInfo = STATUS_LABELS[i.qaStatus] || STATUS_LABELS.PENDIENTE_REVISION;
              return (
                <TableRow key={i.quotationDocumentId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} color="#0288d1">
                      {i.workOrderFolio || (i.workOrderId ? `OT-${i.workOrderId}` : `Cotización ${i.quotationId}`)}
                    </Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{i.companyName || '—'}</Typography></TableCell>
                  <TableCell>
                    <Tooltip title={i.branchId == null ? 'Matriz' : 'Sucursal'}>
                      <Chip
                        icon={i.branchId == null
                          ? <AccountBalanceIcon sx={{ fontSize: '14px !important' }} />
                          : <StoreIcon sx={{ fontSize: '14px !important' }} />}
                        label={i.entityName}
                        size="small"
                        sx={{
                          bgcolor: i.branchId == null ? '#ede7f6' : '#e1f5fe',
                          color: i.branchId == null ? '#4527a0' : '#01579b',
                          fontWeight: 600, fontSize: '0.7rem', height: 22, maxWidth: 220,
                          justifyContent: 'flex-start',
                          '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' }
                        }}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={i.assignedUserName ? 'text.primary' : 'text.secondary'}>
                      {i.assignedUserName || 'Sin asignar'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{docLabel(i.documentKey)}</Typography>
                  </TableCell>
                  <TableCell><Typography variant="body2">{formatDateTime(i.uploadedAt)}</Typography></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip label={statusInfo.label} size="small" sx={{ bgcolor: statusInfo.bg, color: statusInfo.color, fontWeight: 600 }} />
                      {i.contentChanged === true && (
                        <Tooltip title="Reemplazado manualmente con contenido distinto al anterior">
                          <Chip label="Modificado" size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 600 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {i.fileComparisonStatus === 'SAME' ? (
                      <Tooltip title={`El archivo es igual a lo que el sistema generaría hoy (comprobado ${formatDateTime(i.fileComparisonCheckedAt)})`}>
                        <Chip label="✅ Al día" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }} />
                      </Tooltip>
                    ) : i.fileComparisonStatus === 'DIFFERENT' ? (
                      <Tooltip title={`El archivo no coincide con lo que el sistema generaría hoy (comprobado ${formatDateTime(i.fileComparisonCheckedAt)}). Ábrelo para ver qué cambió.`}>
                        <Chip label={`⚠️ Difiere en ${i.fileComparisonDifferences}`} size="small" sx={{ bgcolor: '#fff3e0', color: '#e65100', fontWeight: 700 }} />
                      </Tooltip>
                    ) : i.fileComparisonRunning ? (
                      <Tooltip title="Comparando el archivo contra el machote con los datos actuales…">
                        <Chip icon={<CircularProgress size={10} />} label="Comparando…" size="small" variant="outlined" />
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">Sin comparar</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      {i.dropboxLink && (
                        <Tooltip title="Descargar documento">
                          <IconButton size="small" component="a" href={getDownloadLink(i.dropboxLink)} sx={{ color: '#0288d1' }}>
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Revisar / ver notas">
                        <IconButton size="small" onClick={() => setOpenId(i.quotationDocumentId)} sx={{ color: '#7b1fa2' }}>
                          <FactCheckIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <RevisionCalidadDialog
        open={openId != null}
        quotationDocumentId={openId}
        onClose={() => setOpenId(null)}
        onUpdated={(updated) => {
          setItems(prev => prev.map(x => x.quotationDocumentId === updated.quotationDocumentId ? updated : x));
          fetchMetrics();
        }}
      />

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar(p => ({ ...p, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RevisionCalidad;
